import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Logo, LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Gauge, Activity, ArrowLeft } from "lucide-react";

export default function Login() {
  const { user, login, signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = useState({ name: "", email: "", password: "" });

  const redirectTo = searchParams.get("redirect");

  const getDestination = (role) => {
    if (redirectTo && redirectTo !== "/login") return redirectTo;
    return role === "admin" ? "/admin" : "/portal";
  };

  useEffect(() => {
    if (user) navigate(getDestination(user.role), { replace: true });
  }, [user]);

  useEffect(() => {
    if (searchParams.get("error") === "google") toast.error("Google sign-in didn't complete. Please try again.");
    if (searchParams.get("denied")) toast.error("Your access request was declined. Please contact support.");
  }, []);

  const handleGoogle = () => {
    // Backend runs the Google OAuth flow and redirects back with a session cookie.
    const next = redirectTo && redirectTo !== "/login" ? `?next=${encodeURIComponent(redirectTo)}` : "";
    window.location.href = `${API}/auth/google/login${next}`;
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(loginForm.email, loginForm.password);
      toast.success("Welcome back.");
      navigate(getDestination(u.role));
    } catch (err) {
      const detail = err?.response?.data?.detail || "Invalid credentials.";
      if (err?.response?.status === 403) navigate("/pending");
      else toast.error(detail);
    } finally { setLoading(false); }
  };

  const doSignup = async (e) => {
    e.preventDefault();
    if (signupForm.password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      const data = await signup(signupForm.name, signupForm.email, signupForm.password);
      if (data.status === "pending") {
        navigate("/pending");
      } else {
        toast.success("Welcome to MentorAI Capital.");
        navigate("/portal");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex relative graphite grain text-white p-12 flex-col justify-between">
        <Link to="/"><Logo dark /></Link>
        <div className="relative z-10 max-w-md">
          <h2 className="font-display text-4xl font-semibold tracking-tight leading-tight">The client portal for disciplined trading.</h2>
          <p className="mt-4 text-zinc-300 leading-relaxed">Monitor simulated performance, review every decision with confidence and risk detail, and message our team — all in one place.</p>
          <ul className="mt-8 space-y-4">
            <li className="flex items-center gap-3"><ShieldCheck className="size-5 text-brand" /> Independent risk gates on every trade</li>
            <li className="flex items-center gap-3"><Gauge className="size-5 text-brand" /> Confidence-scored, quality-first setups</li>
            <li className="flex items-center gap-3"><Activity className="size-5 text-brand" /> Live system status & full audit trail</li>
          </ul>
        </div>
        <p className="relative z-10 text-xs text-zinc-400">All portal data is simulated during our revalidation stage.</p>
      </div>

      {/* Right auth panel */}
      <div className="relative flex items-center justify-center p-6 sm:p-12 bg-background">
        <ThemeToggle className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10" />
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Link to="/"><Logo /></Link></div>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="size-4" /> Back to site</Link>
          <LogoMark className="hidden lg:block size-16 rounded-2xl mb-5" />
          <h1 className="font-display text-2xl font-semibold tracking-tight">Client access</h1>
          <p className="mt-2 text-sm text-muted-foreground">Sign in or request an account to continue.</p>

          <Button data-testid="google-login-button" variant="outline" onClick={handleGoogle} className="mt-6 w-full h-11 rounded-xl gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">or</span><div className="h-px flex-1 bg-border" />
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger data-testid="login-tab" value="login">Sign in</TabsTrigger>
              <TabsTrigger data-testid="signup-tab" value="signup">Request access</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <form onSubmit={doLogin} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input data-testid="login-email-input" type="email" required value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="you@firm.com" />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input data-testid="login-password-input" type="password" required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="••••••••" />
                </div>
                <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-primary text-primary-foreground">{loading ? "Signing in…" : "Sign in"}</Button>
              </form>
            </TabsContent>
            <TabsContent value="signup">
              <form onSubmit={doSignup} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Full name</Label>
                  <Input data-testid="signup-name-input" required value={signupForm.name} onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })} placeholder="Jane Doe" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input data-testid="signup-email-input" type="email" required value={signupForm.email} onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })} placeholder="you@firm.com" />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input data-testid="signup-password-input" type="password" required value={signupForm.password} onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })} placeholder="At least 6 characters" />
                </div>
                <Button data-testid="signup-submit-button" type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-[#0EA5A4] hover:bg-[#0B8E8D] text-white">{loading ? "Submitting…" : "Request access"}</Button>
                <p className="text-xs text-muted-foreground text-center">New accounts are reviewed & approved by our team before access is granted.</p>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
